package org.moxultimate.topwithmox.appscrud.api

import io.ktor.client.*
import io.ktor.client.request.*
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.serialization.json.Json
import org.khronos.webgl.Int8Array
import org.moxultimate.topwithmox.apps.data.AppsResponse
import org.moxultimate.topwithmox.apps.data.AppsTagResponse
import org.moxultimate.topwithmox.apps.data.AppsTypeResponse
import org.moxultimate.topwithmox.core.base.BaseAPI.baseAPI
import org.moxultimate.topwithmox.core.handler.APIHandler
import org.moxultimate.topwithmox.core.state.APIState
import org.w3c.files.Blob
import org.w3c.xhr.FormData



/**
 * AppsCRUDRepository
 * Handles all write operations for apps: create, update, delete, and saving tags/types.
 * Read operations live in AppsRepository to keep concerns separate.
 *
 * @param httpClient Ktor HTTP client used for POST, PUT, and DELETE requests.
 */
class AppsCRUDRepository(
    private val httpClient: HttpClient,
) {
    private val baseUrl = "$baseAPI/apps"
    private val tagUrl  = "$baseAPI/apps-tag"
    private val typeUrl = "$baseAPI/apps-type"

    /**
     * createApp
     * Creates a new app entry via POST /apps (multipart/form-data).
     * Uses browser FormData + fetch() — Ktor's multipart builder generates Content-Disposition
     * headers that Spring 6 rejects.
     *
     * @param title App title.
     * @param type App type (Games, Productivity, Wealth, etc.).
     * @param description App description.
     * @param videoLink YouTube or external video URL string.
     * @param imageBytes Raw bytes of the thumbnail image to upload.
     * @return Flow emitting Loading → Success(AppsResponse) or Error.
     */
    fun createApp(
        title: String,
        type: String,
        tags: String,
        description: String,
        videoLink: String,
        imageBytes: ByteArray,
    ): Flow<APIState<AppsResponse>> = flow {
        emit(APIState.Loading)
        emit(performCreateApp(title, type, tags, description, videoLink, imageBytes))
    }

    private suspend fun performCreateApp(
        title: String,
        type: String,
        tags: String,
        description: String,
        videoLink: String,
        imageBytes: ByteArray,
    ): APIState<AppsResponse> {
        return try {
            val form = FormData()
            form.append("title", title)
            form.append("type", type)
            if (tags.isNotBlank()) form.append("tags", tags)
            form.append("description", description)
            form.append("video_link", videoLink)
            val imageBlob = Blob(arrayOf(imageBytes.unsafeCast<Int8Array>().buffer), imageType)
            form.append("image", imageBlob, "image.webp")

            val init: dynamic = js("{}")
            init.method = "POST"
            init.body = form
            val response = window.fetch(baseUrl, init).await()
            if (!response.ok) {
                val code = response.status.toInt()
                val text = response.text().await()
                return APIState.Error(APIHandler.handleErrorMessage(code, text), code)
            }
            APIState.Success(lenientJson.decodeFromString<AppsResponse>(response.text().await()))
        } catch (e: Throwable) {
            APIState.Error(e.message ?: "Create failed")
        }
    }

    /**
     * updateApp
     * Updates an existing app via PUT /apps/{id} (multipart/form-data, all fields optional).
     * Only non-null, non-blank fields and a provided image are sent.
     *
     * @param id App ID to update.
     * @param title Updated title, or null to leave unchanged.
     * @param type Updated type, or null to leave unchanged.
     * @param description Updated description, or null to leave unchanged.
     * @param videoLink Updated video URL string, or null to leave unchanged.
     * @param imageBytes New image bytes, or null to keep existing.
     * @return Flow emitting Loading → Success(AppsResponse) or Error.
     */
    fun updateApp(
        id: Long,
        title: String? = null,
        type: String? = null,
        tags: String? = null,
        description: String? = null,
        videoLink: String? = null,
        imageBytes: ByteArray? = null,
    ): Flow<APIState<AppsResponse>> = flow {
        emit(APIState.Loading)
        emit(performUpdateApp(id, title, type, tags, description, videoLink, imageBytes))
    }

    private suspend fun performUpdateApp(
        id: Long,
        title: String?,
        type: String?,
        tags: String?,
        description: String?,
        videoLink: String?,
        imageBytes: ByteArray?,
    ): APIState<AppsResponse> {
        return try {
            val form = FormData()
            title?.takeIf { it.isNotBlank() }?.let { form.append("title", it) }
            type?.takeIf { it.isNotBlank() }?.let { form.append("type", it) }
            tags?.let { form.append("tags", it) }
            description?.takeIf { it.isNotBlank() }?.let { form.append("description", it) }
            videoLink?.takeIf { it.isNotBlank() }?.let { form.append("video_link", it) }
            imageBytes?.let { bytes ->
                val blob = Blob(arrayOf(bytes.unsafeCast<Int8Array>().buffer), imageType)
                form.append("image", blob, "image.webp")
            }

            val init: dynamic = js("{}")
            init.method = "PUT"
            init.body = form
            val response = window.fetch("$baseUrl/$id", init).await()
            if (!response.ok) {
                val code = response.status.toInt()
                val text = response.text().await()
                return APIState.Error(APIHandler.handleErrorMessage(code, text), code)
            }
            APIState.Success(lenientJson.decodeFromString<AppsResponse>(response.text().await()))
        } catch (e: Throwable) {
            APIState.Error(e.message ?: "Update failed")
        }
    }

    /**
     * deleteApp
     * Deletes an app entry and its S3 files via DELETE /apps/{id}.
     *
     * @param id App ID to delete.
     * @return Flow emitting Loading → Success or Error.
     */
    fun deleteApp(id: Long): Flow<APIState<String>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall { httpClient.delete("$baseUrl/$id") })
    }

    /**
     * saveTag
     * Registers a new tag via POST /apps-tag?tag=String (idempotent).
     *
     * @param tag Tag label to persist.
     * @return Flow emitting Loading → Success(AppsTagResponse) or Error.
     */
    fun saveTag(tag: String): Flow<APIState<AppsTagResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.post(tagUrl) { parameter("tag", tag) }
        })
    }

    /**
     * saveType
     * Registers a new type via POST /apps-type?type=String (idempotent).
     *
     * @param type Type label to persist.
     * @return Flow emitting Loading → Success(AppsTypeResponse) or Error.
     */
    fun saveType(type: String): Flow<APIState<AppsTypeResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.post(typeUrl) { parameter("type", type) }
        })
    }
}

/** Lenient JSON parser — tolerates extra fields returned by the server. */
private val lenientJson = Json { ignoreUnknownKeys = true }

/** MIME type options for thumbnail images. */
private val imageType: dynamic = js("{type: 'image/webp'}")
